// src/pages/Platform/DemoRequests.js
//
// The "Request a Demo" funnel's own queue — every submission from
// posflect.com's own DemoRequestForm.js lands here first (SubmitDemoRequestHandler,
// internal/handlers/demo_request_handler.go). Same plain-markup, no-i18n,
// no-DataTable.jsx style as Companies.js (see that file's own doc comment
// for why) — status filter + search + pagination shape mirrors
// ListDemoRequestsHandler exactly. Row actions: mark contacted/rejected
// (small reason modal), and "Convert to company" — reuses the exact same
// ProvisioningProgress component Companies.js's own AddCompanyModal uses,
// since converting kicks off the identical background ProvisionCompany call.
import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDemoRequests,
  updateDemoRequestStatus,
  convertDemoRequest,
} from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";
import ProvisioningProgress from "../../components/Platform/ProvisioningProgress";

const STATUS_STYLES = {
  new: { bg: "#dbeafe", fg: "#1e40af", label: "New" },
  contacted: { bg: "#fef3c7", fg: "#92400e", label: "Contacted" },
  converted: { bg: "#dcfce7", fg: "#166534", label: "Converted" },
  rejected: { bg: "#fee2e2", fg: "#991b1b", label: "Rejected" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: "#e2e8f0", fg: "#334155", label: status };
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

// slugify mirrors the same "company name -> a plausible subdomain" guess
// AddCompanyModal's own subdomain field expects a human to type by hand —
// here it's only ever a STARTING POINT for the convert modal's own
// subdomain field (which stays fully editable, and is re-validated for
// real, server-side, at convert time — see ConvertDemoRequestToCompanyHandler's
// own doc comment on why PreferredSubdomain was never availability-checked
// at submission).
function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

// StatusReasonModal handles both "mark contacted" (no reason needed) and
// "mark rejected" (an optional reason an admin might want on record) — one
// small modal instead of two nearly-identical ones.
function StatusReasonModal({ status, onClose, onConfirm, saving }) {
  const [reason, setReason] = useState("");
  const isReject = status === "rejected";

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 380 }}>
        <h3 style={{ marginTop: 0 }}>{isReject ? "Reject demo request" : "Mark as contacted"}</h3>
        {isReject && (
          <>
            <label style={labelStyle}>Reason (optional)</label>
            <textarea
              style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this request isn't being pursued..."
            />
          </>
        )}
        {!isReject && (
          <p style={{ fontSize: 13, color: "#64748b" }}>
            This just marks the request as followed up on — it doesn't send anything automatically.
          </p>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button type="button" style={secondaryBtnStyle} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" style={primaryBtnStyle} onClick={() => onConfirm(reason)} disabled={saving}>
            {saving ? "Saving..." : isReject ? "Reject" : "Mark contacted"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ConvertModal is the "when I need to fulfill the request I take the
// action from the panel" step the user asked for — every field is
// prefilled from the DemoRequest itself but stays fully editable (see
// ConvertDemoRequestToCompanyHandler's own doc comment on why). On success,
// shows the exact same ProvisioningProgress + one-time credentials view
// AddCompanyModal's own success screen shows for a manually-added company.
function ConvertModal({ token, request, onClose, onConverted, onViewCompany }) {
  const [form, setForm] = useState({
    name: request.company_name,
    subdomain: slugify(request.preferred_subdomain || request.company_name),
    contact_email: request.contact_email,
    admin_email: "",
    subscription_days: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form };
      payload.subscription_days = form.subscription_days ? Number(form.subscription_days) : 0;
      const res = await convertDemoRequest(token, request.id, payload);
      setResult(res);
      onConverted();
    } catch (err) {
      setError(err.message || "Failed to convert this demo request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {result ? (
          <>
            <h3 style={{ marginTop: 0 }}>Company created</h3>
            <p style={{ fontSize: 14, color: "#475569" }}>
              This demo request has been converted — provisioning has started in the background.
            </p>
            <ProvisioningProgress
              token={token}
              companyId={result.company.id}
              initialCompany={result.company}
              onViewCompany={onViewCompany}
            />
            <div style={credentialsBoxStyle}>
              <div><strong>Login URL:</strong> https://{result.company.subdomain}.posflect.com</div>
              <div><strong>Admin email:</strong> {form.admin_email}</div>
              <div>
                <strong>Admin password:</strong> {result.admin_password}
                {result.generated_password && " (auto-generated)"}
              </div>
            </div>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>
              This password is shown only once. It has also been emailed to the company's contact address, if SMTP is configured.
            </p>
            <button style={primaryBtnStyle} onClick={onClose}>Done</button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 style={{ marginTop: 0 }}>Convert to company</h3>
            <p style={{ fontSize: 13, color: "#64748b", marginTop: -4 }}>
              Business type: <strong>{request.business_type}</strong> · Country: <strong>{request.country || "—"}</strong>
              {request.message && <><br />"{request.message}"</>}
            </p>

            <label style={labelStyle}>Company name</label>
            <input style={inputStyle} value={form.name} onChange={set("name")} required />

            <label style={labelStyle}>Subdomain</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={form.subdomain}
                onChange={(e) => setForm((f) => ({ ...f, subdomain: e.target.value.toLowerCase() }))}
                required
              />
              <span style={{ fontSize: 13, color: "#64748b" }}>.posflect.com</span>
            </div>

            <label style={labelStyle}>Contact email</label>
            <input style={inputStyle} type="email" value={form.contact_email} onChange={set("contact_email")} required />

            <label style={labelStyle}>Admin login email</label>
            <input style={inputStyle} type="email" value={form.admin_email} onChange={set("admin_email")} required />

            <label style={labelStyle}>Subscription length (days)</label>
            <input
              style={inputStyle}
              type="number"
              min="1"
              value={form.subscription_days}
              onChange={set("subscription_days")}
              placeholder="Leave blank to use the default trial length"
            />

            {error && <div style={errorBoxStyle}>{error}</div>}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button type="button" style={secondaryBtnStyle} onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" style={primaryBtnStyle} disabled={saving}>
                {saving ? "Creating..." : "Create company"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function DemoRequests() {
  const { token, permissions } = useContext(PlatformAuthContext);
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [statusModal, setStatusModal] = useState(null); // { request, status }
  const [convertRequest, setConvertRequest] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);

  const canManage = permissions.includes("demorequest_manage");
  const canConvert = permissions.includes("demorequest_convert");

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (status) params.status = status;
      const res = await getDemoRequests(token, params);
      setRequests(res.data || []);
      setMeta(res.meta || { total: 0, page: 1, limit: 20 });
    } catch (err) {
      console.error("Failed to load demo requests:", err.message);
    } finally {
      setLoading(false);
    }
  }, [token, search, status]);

  useEffect(() => {
    load(1);
  }, [load]);

  const handleStatusConfirm = async (reason) => {
    setSavingStatus(true);
    try {
      await updateDemoRequestStatus(token, statusModal.request.id, statusModal.status, reason);
      setStatusModal(null);
      await load(meta.page);
    } catch (err) {
      alert(err.message || "Failed to update this demo request");
    } finally {
      setSavingStatus(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 20)));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Demo requests</h2>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input
          style={{ ...inputStyle, maxWidth: 260 }}
          placeholder="Search by company, contact, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={{ ...inputStyle, maxWidth: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              {["Company", "Contact", "Business type", "Country", "Status", "Requested", ""].map((h, i) => (
                <th key={i} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={tdEmptyStyle}>Loading...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={7} style={tdEmptyStyle}>No demo requests found.</td></tr>
            ) : (
              requests.map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{r.company_name}</div>
                    {r.preferred_subdomain && (
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>{r.preferred_subdomain}.posflect.com (requested)</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <div>{r.contact_name}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>{r.contact_email}</div>
                  </td>
                  <td style={tdStyle}>{r.business_type}</td>
                  <td style={tdStyle}>{r.country || "—"}</td>
                  <td style={tdStyle}>
                    <StatusBadge status={r.status} />
                    {r.status === "rejected" && r.rejection_reason && (
                      <div style={{ fontSize: 11, marginTop: 4, color: "#991b1b" }}>{r.rejection_reason}</div>
                    )}
                  </td>
                  <td style={tdStyle}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    {r.status === "new" && canManage && (
                      <>
                        <button style={linkBtnStyle} onClick={() => setStatusModal({ request: r, status: "contacted" })}>
                          Mark contacted
                        </button>
                        <button style={linkBtnStyle} onClick={() => setStatusModal({ request: r, status: "rejected" })}>
                          Reject
                        </button>
                      </>
                    )}
                    {r.status === "contacted" && canManage && (
                      <button style={linkBtnStyle} onClick={() => setStatusModal({ request: r, status: "rejected" })}>
                        Reject
                      </button>
                    )}
                    {(r.status === "new" || r.status === "contacted") && canConvert && (
                      <button style={linkBtnStyle} onClick={() => setConvertRequest(r)}>
                        Convert to company
                      </button>
                    )}
                    {r.status === "converted" && r.converted_company_id && (
                      <button style={linkBtnStyle} onClick={() => navigate(`/platform/companies/${r.converted_company_id}`)}>
                        View company
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => load(p)}
              style={{
                ...secondaryBtnStyle,
                background: p === meta.page ? "#0f172a" : "#fff",
                color: p === meta.page ? "#fff" : "#334155",
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {statusModal && (
        <StatusReasonModal
          status={statusModal.status}
          saving={savingStatus}
          onClose={() => setStatusModal(null)}
          onConfirm={handleStatusConfirm}
        />
      )}

      {convertRequest && (
        <ConvertModal
          token={token}
          request={convertRequest}
          onClose={() => {
            setConvertRequest(null);
            load(meta.page);
          }}
          onConverted={() => load(meta.page)}
          onViewCompany={(id) => {
            setConvertRequest(null);
            navigate(`/platform/companies/${id}`);
          }}
        />
      )}
    </div>
  );
}

const thStyle = { padding: "12px 16px", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase" };
const tdStyle = { padding: "14px 16px", fontSize: 14, color: "#334155", verticalAlign: "top" };
const tdEmptyStyle = { padding: 40, textAlign: "center", color: "#94a3b8" };
const inputStyle = {
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = {
  background: "#0f172a",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "10px 16px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};
const secondaryBtnStyle = {
  background: "#fff",
  color: "#334155",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "10px 16px",
  fontSize: 14,
  cursor: "pointer",
};
const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "#2563eb",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  marginLeft: 10,
};
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const modalStyle = {
  background: "#fff",
  borderRadius: 12,
  padding: 24,
  width: 420,
  maxWidth: "90vw",
  maxHeight: "90vh",
  overflowY: "auto",
};
const errorBoxStyle = {
  background: "#fee2e2",
  color: "#991b1b",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 13,
  marginTop: 12,
};
const credentialsBoxStyle = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: 12,
  fontSize: 13,
  lineHeight: 1.8,
  marginBottom: 12,
};
